import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@/assets/icons/ArrowForwardIcon";
import ArrowUpDownIcon from "@/assets/icons/ArrowUpDownIcon";
import CopyIcon from "@/assets/icons/CopyIcon";
import ShareIcon from "@/assets/icons/ShareIcon";
import ProviderIcon from "../ProviderIcon";
import { ModelTypeBadge, ModelVisibilityBadge } from "./ModelBadges";
import type { CatalogOrder, LLMModel, TranslateFn } from "../types";

const TABLE_CELL_BORDER_CLASS = "!border-b !border-[var(--border)]";

type CatalogTableProps = {
  t: TranslateFn;
  isRTL: boolean;
  loading: boolean;
  models: LLMModel[];
  currentUserId?: string;
  order: CatalogOrder;
  onSortName: () => void;
  onShare: (event: React.MouseEvent<HTMLElement>, model: LLMModel) => void;
  onCopyEndpoint: (endpoint: string) => void;
  onOpenDetail: (model: LLMModel) => void;
};

const LoadingRows = ({ isRTL }: { isRTL: boolean }) => (
  <>
    {Array.from({ length: 10 }, (_, index) => index + 1).map((num) => (
      <TableRow
        key={num}
        className="group bg-[var(--background)] hover:bg-[var(--sidebar-primary)] transition duration-200"
      >
        <TableCell
          className={TABLE_CELL_BORDER_CLASS}
          align={isRTL ? "right" : "left"}
        >
          <Box className="flex items-center gap-2">
            <Box className="relative">
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Box>
              <Skeleton variant="text" width="100px" height="20px" />
              <Skeleton variant="text" width="200px" height="16px" />
            </Box>
          </Box>
        </TableCell>
        <TableCell
          className={TABLE_CELL_BORDER_CLASS}
          align={isRTL ? "right" : "left"}
        >
          <Skeleton variant="rounded" width="125px" height="30px" />
        </TableCell>
        <TableCell
          className={TABLE_CELL_BORDER_CLASS}
          align={isRTL ? "right" : "left"}
        >
          <Skeleton variant="text" width="300px" height="20px" />
        </TableCell>
        <TableCell
          className={TABLE_CELL_BORDER_CLASS}
          align={isRTL ? "left" : "right"}
        >
          <Skeleton variant="rectangular" width="16px" height="16px" />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const CatalogTable = ({
  t,
  isRTL,
  loading,
  models,
  currentUserId,
  order,
  onSortName,
  onShare,
  onCopyEndpoint,
  onOpenDetail,
}: CatalogTableProps) => (
  <TableContainer
    component={Paper}
    className="bg-transparent rounded-xl border-0 shadow-none"
  >
    <Table>
      <TableHead className="bg-[var(--card)]">
        <TableRow>
          <TableCell
            className={TABLE_CELL_BORDER_CLASS}
            align={isRTL ? "right" : "left"}
          >
            <TableSortLabel
              data-testid="ai-catalog-sort-name-button"
              active
              direction={order}
              onClick={onSortName}
              className="[&_.MuiTableSortLabel-icon]:hidden"
            >
              <Box className="flex items-center gap-2">
                <Typography className="text-[var(--foreground)]">
                  {t("predefinedModels.modelInformation")}
                </Typography>
                <ArrowUpDownIcon className="text-[var(--muted-foreground)]" />
              </Box>
            </TableSortLabel>
          </TableCell>
          <TableCell
            className={TABLE_CELL_BORDER_CLASS}
            align={isRTL ? "right" : "left"}
          >
            <Typography className="text-[var(--foreground)]">
              {t("predefinedModels.type")}
            </Typography>
          </TableCell>
          <TableCell
            className={TABLE_CELL_BORDER_CLASS}
            align={isRTL ? "right" : "left"}
          >
            <Typography className="text-[var(--foreground)]">
              {t("registerModelForRag.endpointUrl")}
            </Typography>
          </TableCell>
          <TableCell
            className={TABLE_CELL_BORDER_CLASS}
            align={isRTL ? "left" : "right"}
          />
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <LoadingRows isRTL={isRTL} />
        ) : models.length ? (
          models.map((model, index) => (
            <TableRow
              key={model.id}
              className="group bg-[var(--background)] hover:bg-[var(--sidebar-primary)] transition duration-200"
            >
              <TableCell
                className={TABLE_CELL_BORDER_CLASS}
                align={isRTL ? "right" : "left"}
              >
                <Box className="flex items-center gap-2">
                  <Box className="relative">
                    <ProviderIcon provider={model.provider} />
                    <ModelVisibilityBadge model={model} />
                  </Box>
                  <Box>
                    <Typography className="text-[var(--foreground)] text-sm font-normal">
                      {model.name}
                    </Typography>
                    <Typography className="text-[var(--muted-foreground)] text-xs font-normal">
                      {model.id}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell
                className={TABLE_CELL_BORDER_CLASS}
                align={isRTL ? "right" : "left"}
              >
                <ModelTypeBadge model={model} t={t} />
              </TableCell>
              <TableCell
                className={TABLE_CELL_BORDER_CLASS}
                align={isRTL ? "right" : "left"}
              >
                <Typography className="text-[var(--muted-foreground)] text-xs font-normal truncate max-w-[250px]">
                  {model.endpoint}
                </Typography>
              </TableCell>
              <TableCell
                className={TABLE_CELL_BORDER_CLASS}
                align={isRTL ? "left" : "right"}
              >
                <Box className="flex items-center justify-end gap-2">
                  {!model.is_public && model.user_id === currentUserId && (
                    <Box
                      data-testid={`ai-catalog-share-model-${index}`}
                      className="cursor-pointer"
                      onClick={(event) => onShare(event, model)}
                    >
                      <ShareIcon className="text-[var(--secondary-foreground)]" />
                    </Box>
                  )}
                  {model.endpoint && (
                    <Box
                      data-testid={`ai-catalog-copy-endpoint-${index}`}
                      className="cursor-pointer"
                      onClick={() => onCopyEndpoint(model.endpoint || "")}
                    >
                      <CopyIcon className="text-[var(--secondary-foreground)]" />
                    </Box>
                  )}
                  <Box
                    data-testid={`ai-catalog-open-detail-${index}`}
                    className="cursor-pointer opacity-0 group-hover:opacity-100 transition"
                    onClick={() => onOpenDetail(model)}
                  >
                    <ArrowForwardIcon className="text-[var(--secondary-foreground)] rtl:scale-x-[-1]" />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={4} align="center">
              {t("common.noDataAvailable")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default CatalogTable;
